import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

import { esntialEligibilityFetcher } from 'reaxl-ecommerce';

import inventoryDuck from '@/ducks/inventoryDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';

/**
 * Custom hook to handle Esntial eligibility checking on SRP
 * @param {Object} listing - The vehicle listing object
 * @param {boolean} isSponsoredExperience - Whether this is a sponsored experience
 * @returns {Object} { isEligible, isLoading, error }
 */
export default function useEsntialEligibility(listing, isSponsoredExperience) {
    const [isEligible, setIsEligible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const dispatch = useDispatch();
    const {
        esntial_eligibility_v2: [enableV2],
    } = useFeatures(['esntial_eligibility_v2']);

    const { esntial, id, vin } = listing || {};
    const existingDeal = useSelector((state) => paymentsDuck?.selectors?.getDealByListingVin(state, vin)) || {};

    const disableEsntial = useCallback(() => {
        dispatch(inventoryDuck.creators.updateInventoryItem(id, {
            esntial: undefined,
            ecommWebsite: undefined,
        }));
        setIsEligible(false);
    }, [dispatch, id]);

    useEffect(() => {
        if (!isSponsoredExperience || !listing || !esntial?.ecommWebsite) {
            setIsEligible(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        disableEsntial();

        const {
            year,
            make,
            model,
            trim: listingTrim,
            mileage: listingMileage,
            listingType,
            pricingDetail = {},
            owner: listingOwner,
            marketExtension: { parentOwnerId: parentId } = {},
        } = listing;

        const { incentive, msrp, salePrice } = pricingDetail;
        const isCertified = listingType?.toLowerCase() === 'certified';
        const listingPrice = incentive || salePrice || msrp;
        const mileage = typeof listingMileage === 'object' ? listingMileage?.value : listingMileage;
        const ownerId = parentId || (typeof listingOwner === 'object' ? listingOwner?.id : listingOwner);
        const trim = typeof listingTrim === 'object' ? listingTrim?.name : listingTrim;
        const vehicleCondition = listingType?.toLowerCase() === 'certified' ? 'used' : listingType?.toLowerCase();

        const payloadItems = {
            isCertified,
            listingPrice,
            make: typeof make === 'object' ? make?.name : make,
            mileage,
            model: typeof model === 'object' ? model?.name : model,
            msrp,
            ownerId,
            trim,
            vehicleCondition,
            vin,
            year,
        };

        // Uses v1 endpoint if FF is not enabled
        const v1EsntialEndpoint = esntial?.eligibilityService?.replace('vehicle/v2/verify', 'search/verify');
        const eligibilityURL = enableV2 ? esntial?.eligibilityService : v1EsntialEndpoint;
        const isV2Enabled = enableV2 && eligibilityURL?.includes('/v2/');

        esntialEligibilityFetcher(eligibilityURL, isV2Enabled ? payloadItems : vin)
            .then((response) => {
                setIsLoading(false);

                if (response?.totalMatched >= 1 || response?.[0]?.sellable) {
                    // Enable ecommWebsite and dealerPreferredCTA
                    dispatch(inventoryDuck.creators.updateInventoryItem(id, {
                        esntial,
                        ecommWebsite: esntial.ecommWebsite,
                    }));

                    setIsEligible(true);

                    // Analytics tracking
                    if (window?.newrelic) {
                        window.newrelic.addPageAction('ThirdPartyAction', {
                            id,
                            [isV2Enabled ? 'payloadItems' : 'vin']: isV2Enabled ? payloadItems : vin,
                            component: 'useEsntialEligibilitySRP',
                            message: `Vehicle eligible for SRP Esntial${isV2Enabled ? ' V2' : ''}`,
                        });
                    }
                } else {
                    let message = 'Vehicle ineligible for SRP Esntial';
                    if (response?.circuitBreakerOpen) {
                        message = 'Esntial Eligibility Circuit Breaker Open; displaying as vehicle ineligible for Esntial';
                    }

                    setError(message);

                    // Analytics tracking
                    if (window?.newrelic) {
                        window.newrelic.addPageAction('ThirdPartyError', {
                            id,
                            [isV2Enabled ? 'payloadItems' : 'vin']: isV2Enabled ? payloadItems : vin,
                            component: 'useEsntialEligibilitySRP',
                            message: `${message}${isV2Enabled ? ' V2' : ''}`,
                        });
                    }
                }
            })
            .catch((err) => {
                setIsLoading(false);
                setError(err.message || 'Eligibility check failed');
                console.error('Esntial eligibility check failed:', err);
            });

    }, [
        vin,
        existingDeal.isEcommerce,
        isSponsoredExperience,
        esntial?.ecommWebsite,
        listing?.id,
        enableV2,
        disableEsntial,
        dispatch,
        esntial,
        id,
        listing,
    ]);

    return { isEligible, isLoading, error };
}
